import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress,
  Container, MenuItem, Select, Stack, Tab, Tabs,
  Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import Iconify from '../../components/iconify';
import { useProject } from '../../hooks/ProjectContext';
import { useRole, ROLE_LABELS } from '../../hooks/useRole';
import { useSearchParams } from 'react-router-dom';
import ProjectSetupTab from '../../components/settings/ProjectSetupTab';
import ProjectTeamTab from '../../components/settings/ProjectTeamTab';
import GeometryReviewTab from '../../components/settings/GeometryReviewTab';

const ROLE_COLOR = { admin: 'error', super_user: 'warning', user: 'primary', project_manager: 'primary', site_manager: 'info' };

function roleChip(roleName) {
  if (!roleName) return <Chip label="No Role" size="small" />;
  return <Chip label={ROLE_LABELS[roleName] || roleName} size="small" color={ROLE_COLOR[roleName] || 'default'} />;
}

function bearerHeaders() {
  const tkn = localStorage.getItem('tkn');
  return { 'Content-Type': 'application/json', ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) };
}

// -- Site Managers tab --------------------------------------------------------

function SiteManagersTab({ projectId, canManage }) {
  const { level } = useRole();
  const [sites, setSites] = useState([]);
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [newUserId, setNewUserId] = useState('');
  const [newSiteId, setNewSiteId] = useState('');

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setMsg('');
    try {
      const [sitesRes, eligRes, assignRes] = await Promise.all([
        fetch(`/api/access/projects/${projectId}/sites`, { headers: bearerHeaders() }),
        fetch(`/api/access/projects/${projectId}/eligible-users`, { headers: bearerHeaders() }),
        fetch(`/api/access/projects/${projectId}/site-managers`, { headers: bearerHeaders() }),
      ]);
      setSites(sitesRes.ok ? (await sitesRes.json()).sites || [] : []);
      const eligData = eligRes.ok ? await eligRes.json() : {};
      setEligibleUsers(eligData.users || []);
      if (eligData.personalDomain) setMsg('Your email domain is a personal provider (e.g. Gmail). Ask an Admin or Super User to assign roles.');
      setAssignments(assignRes.ok ? (await assignRes.json()).assignments || [] : []);
    } catch {
      setMsg('Failed to load settings.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const assignSiteManager = async () => {
    if (!newUserId || !newSiteId) return;
    setMsg('');
    try {
      const res = await fetch(`/api/access/projects/${projectId}/site-managers`, {
        method: 'POST', headers: bearerHeaders(),
        body: JSON.stringify({ userId: newUserId, siteId: newSiteId }),
      });
      const j = await res.json();
      if (!res.ok || j.ok === false) throw new Error(j.message || `HTTP ${res.status}`);
      setMsg('Site Manager assigned.');
      setNewUserId(''); setNewSiteId('');
      load();
    } catch (e) { setMsg(e.message); }
  };

  const removeAssignment = async (userId, siteId) => {
    setMsg('');
    try {
      const res = await fetch(`/api/access/user-sites/${userId}/${siteId}`, {
        method: 'DELETE', headers: bearerHeaders(),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j.ok === false) throw new Error(j.message || `HTTP ${res.status}`);
      setMsg('Assignment removed.');
      load();
    } catch (e) { setMsg(e.message); }
  };

  if (loading) return <CircularProgress sx={{ mt: 4 }} />;

  return (
    <>
      {msg && (
        <Typography variant="body2" sx={{ mb: 2, color: msg.toLowerCase().includes('fail') || msg.toLowerCase().includes('error') ? 'error.main' : 'success.main' }}>
          {msg}
        </Typography>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Site Managers</Typography>
          {assignments.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No site managers assigned yet.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Site</TableCell>
                  <TableCell>Role</TableCell>
                  {canManage && <TableCell />}
                </TableRow>
              </TableHead>
              <TableBody>
                {assignments.map((a) => (
                  <TableRow key={`${a.user_id}-${a.site_id}`}>
                    <TableCell>{a.user_email}</TableCell>
                    <TableCell>{a.site_name}</TableCell>
                    <TableCell>{roleChip(a.role_name)}</TableCell>
                    {canManage && (
                      <TableCell>
                        <Button size="small" color="error" onClick={() => removeAssignment(a.user_id, a.site_id)}>Remove</Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {canManage && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>Assign Site Manager</Typography>
            {eligibleUsers.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No eligible users found.
                {level <= 30 && level > 20 && ' Only users from your organization without a role can be assigned.'}
              </Typography>
            ) : (
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>User</Typography>
                    <Select size="small" fullWidth value={newUserId} onChange={(e) => setNewUserId(e.target.value)} displayEmpty>
                      <MenuItem value=""><em>Select user...</em></MenuItem>
                      {eligibleUsers.map((u) => (
                        <MenuItem key={u.user_id} value={u.user_id}>
                          {u.user_email}{u.user_nick_name ? ` (${u.user_nick_name})` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Site</Typography>
                    <Select size="small" fullWidth value={newSiteId} onChange={(e) => setNewSiteId(e.target.value)} displayEmpty>
                      <MenuItem value=""><em>Select site...</em></MenuItem>
                      {sites.map((s) => (
                        <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Stack>
                <Button variant="contained" disabled={!newUserId || !newSiteId} onClick={assignSiteManager}
                  startIcon={<Iconify icon="eva:person-add-outline" />} sx={{ alignSelf: 'flex-start' }}>
                  Assign as Site Manager
                </Button>
              </Stack>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
}


// -- Main page ----------------------------------------------------------------

export default function ProjectSettingsPage() {
  const { selectedProject } = useProject();
  const { level } = useRole();
  const [searchParams] = useSearchParams();
  // Deep link: /project/settings?tab=geometry (used by Library + old bookmarks).
  const [tab, setTab] = useState(searchParams.get('tab') === 'geometry' ? 2 : 0);

  if (!selectedProject) {
    return (
      <Container maxWidth="md">
        <Helmet><title>Project Settings | TERRRA</title></Helmet>
        <Typography variant="h4" sx={{ mb: 3 }}>Project Settings</Typography>
        <Typography color="text.secondary">Select a project first.</Typography>
      </Container>
    );
  }

  const canManage = level <= 30;

  return (
    <>
      <Helmet><title>Project Settings | TERRRA</title></Helmet>
      <Container maxWidth="lg">
        <Typography variant="h4" sx={{ mb: 1 }}>Project Settings</Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
          {selectedProject.name}
        </Typography>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Setup" />
          <Tab label="Team" />
          <Tab label="Geometry Review" />
        </Tabs>

        {tab === 0 && (
          canManage
            ? <ProjectSetupTab projectId={selectedProject.id} />
            : <Typography color="text.secondary">You don't have permission to edit project setup.</Typography>
        )}
        {tab === 1 && <ProjectTeamTab projectId={selectedProject.id} />}
        {tab === 2 && <GeometryReviewTab />}
      </Container>
    </>
  );
}
